/** @module csi/slicer/slicerWidget */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dnd/Manager",
	"dojo/dom-style",
	"dojo/store/Memory",
	"dijit/layout/BorderContainer",
	"dijit/layout/ContentPane",
	"csi",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/action",
	"csi/constants",
	"csi/def",
	"csi/def/Action",
	"csi/enums",
	"csi/heading/_base",
	"csi/heading/Specs",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/page",
	"csi/prefs/PreferenceView",
	"csi/resolver",
	"csi/middleware/rollup/rollupService",
	"csi/slicer/_base",
	"csi/slicer/SlicerDataAccessAdapter",
	"csi/slicer/graphs/Widget",
	"csi/store/Extract",
	"csi/store/OrderedMemory",
	"csi/view/_CustomViewMixin",
	"csi/view/_CustomViewQueryMixin",
	"csi/view/_QueryMixin",
	"csi/view/PluginFactory",
	"csi/view/ToolbarFilter",
	"csi/view/ToolbarPluginButton",
	"csi/view/Widget",
	"csi/xml",
	"csi/xml/ExtractQuery",
	"./slicer",
	"./slicerConfig",
	"./slicerEditor",

	"./slicerExcel",
	"./graphs/ToolbarPluginGraphMetric",
	"./graphs/ToolbarPluginGraphSettings",
	"./graphs/ToolbarPluginToggleGraphs"
], function(declare, lang, aspect, dndManager, domStyle, Memory, BorderContainer, ContentPane, csi, storage, _, csiAction, constants, def, Action, enums,
	csiHeading, Specs, html, locale, message, page, PreferenceView, resolver, {evaluate}, csiSlicer, SlicerDataAccessAdapter, GraphsWidget, Extract, OrderedMemory, _CustomViewMixin,
	_CustomViewQueryMixin, _QueryMixin, PluginFactory, ToolbarFilter, ToolbarPluginButton, Widget, xml, ExtractQuery, Slicer, SlicerConfig, SlicerEditor) {

	/**
	 * Register action "Export to Excel"
	 */
	csiAction.registerControllers("Slicer", {
		SlicerExcel: "csi.slicer.slicerExcelController"
	});

	/**
	 * Toolbar Plugin - Slicer filter
	 */
	PluginFactory.declare("slicerFilter", ToolbarFilter, {
		postCreate: function() {
			// summary: this is necessary for Slicer to handle ref attributes correctly - we won't know the name for the CNL
			// until after the data is loaded
			this.inherited(arguments);
			this.own(aspect.after(this.getView(), "_postView", lang.hitch(this, this._updateTooltipNode)));
		}
	});

	/** @property {string} SLICER_EDITOR_VISIBLE - Local storage ID for slicer editor visible property */
	var SLICER_EDITOR_VISIBLE = "slicerEditorVisible";

	/**
	 * Toolbar plugin for "Show Slicer editor" button
	 */
	PluginFactory.declare("slicerEditor", ToolbarPluginButton, {
		constructor: function() {
			this.defAction = lang.mixin(new Action(), {
				m_id: "showSlicerEditor",
				m_typeControl: def.ACTION_ICON,
				m_tooltip: locale.resource("SlicerEditor"),
				m_onAction: "onActionShowEditor"
			});
			this.defAction.resolve();
		},
		enablePlugin: function(ctx) {
			// Disable if context is not ready
			// Disable if view was not built due to server/store errors
			// Enable if view was not build due to missconfiguration, allow user to reconfigure
			const bDisabled = !ctx || (!ctx.view._viewBuilt && !this.getView().isEmpty());
			if (bDisabled) {
				this._setDisabled(this, true);
			} else {
				this.inherited(arguments);
			}
		}
	});

	/**
	 * Set of Custom View settings
	 */
	var _Preferences = declare(PreferenceView, {
		customOptions: [
			// Old Options, not used in new Slicer. Keep for compatibility.
			{name: "collapseColumnTotal", value: false},
			{name: "collapseRowTotal", value: false},
			{name: "columnCompareKey", value: null}, // old but might re-use
			{name: "columnTotalKey", value: null},
			{name: "rowTotalKey", value: null},

			// Options used in new Slicer
			{name: "columnKeys", personalized: true, value: []},
			{name: "columnSizes", personalized: true, value: {}},
			{name: "compareTo", personalized: true, value: {}},
			{name: "filterRemember", personalized: false, value: true},
			{name: "flatSlicer", personalized: true, value: false},
			{name: "expandAll", personalized: true, value: false},
			{name: "graphMetricKey", personalized: true, value: ""},
			{name: "graphSettings", personalized: true, value: GraphsWidget.DEFAULT_SETTINGS},
			{name: "metricKeys", personalized: true, value: []},
			{name: "metricsHl", personalized: true, value: {}},
			{name: "metricsHlVisible", personalized: true, value: {}},
			{name: "metricsPos", personalized: true, value: constants.SLICER_MAX_METRICS_POS},
			{name: "rowKeys", personalized: true, value: []},
			{name: "showGraphs", personalized: true, value: false},
			{name: "showTotals", personalized: true, value: {}},
			{name: "stickyRows", personalized: true, value: false},
		]
	});

	/**
	 * Do required dnd initialization
	 */
	function dndInit() {
		// If touch device detected, Dojo activates touch regime by defining has("touch") - see dojo/has.js
		// In touch regime X and Y offsets of the pointer relative to the dragging object are set to {0, -64} - see dojo/dnd/Manager.js
		// If device has both mouse and touch - it will conflict and mouse dragging will be broken.
		// Enforcing mouse mode for slicer drag-drop operations:
		dndManager.manager().OFFSET_X = 16;
		dndManager.manager().OFFSET_Y = 16;
	}

	/**
	 * Return OrderedMemory store of classifiers that can be sorted alphabetically or ordered
	 * @param {Memory} store
	 * @param {string[]} keys
	 * @return {OrderedMemory}
	 */
	function _getStore(store, keys) {
		// summary:
		if (!store) return null;

		var idProperty = store.idProperty;
		var indexMap = csi.hashArray(keys, null, Number);
		var items = store.query();
		var n = items.length;

		return new OrderedMemory({
			idProperty: idProperty,
			idOrder: "order",
			data: _.map(items, function(item, i) {
				var index = indexMap[item[idProperty]];
				item = _.clone(item);

				if (index == null) {
					item.active = false;
					item.order = n + i;
				} else {
					item.active = true;
					item.order = index;
				}

				return item;
			})
		});
	}

	return declare([Widget, _CustomViewMixin, _QueryMixin, _CustomViewQueryMixin], {
		bResetStore: false,
		enableCustomViewDialog: false,
		plugins: ['fullScreen', 'SlicerExcel', 'slicerEditor', 'slicerFilter', 'refresh', '|', 'slicerToggleGraphs', 'slicerGraphMetric', 'slicerGraphSettings'],
		pluginsRight: ['fullScreen', 'SlicerExcel'],
		prefClass: _Preferences,
		slicerContainer: null,
		baseClass: 'csi-slicer-widget',

		// Any[][]: 2-dimensional array of data filtered by values in toolbar plugin
		_filteredData: null,

		/** Action "On property change", invoked when claissifers or metrics setting changed */
		_propertyAction: null,

		/** @property {Object} _lastRequstKeys - remeber which keys we had ordered in last sererr request */
		_lastRequestKeys: null,

		/** @property {Object} _cfgHandle - Configuration subscription handle */
		_cfgHandle: null,

		/** @property {boolean} _requestErrorProcessed - for internal use - flag to prevent endless circle */
		_requestErrorProcessed: false,

		/** @property {SlicerConfig} _slicerConfig */
		_slicerConfig: null,

		/** @property {Slicer} _slicer - slicer grid object */
		_slicer: null,

		/** @property {SlicerEditor} _slicerEditor - reference to slicer editor */
		_slicerEditor: null,

		/** @property {array} _specs - array of classifier and metrics specs */
		_specs: null,

		_viewBuilt: false,

		/**
		 * Object: various settings for graphs widget
		 */
		graphSettings: GraphsWidget.DEFAULT_SETTINGS,

		startup: function() {
			const res = this.inherited(arguments);

			if (!this.slicerFilter) { // TBD: startup being called twice
				this.slicerFilter = this.getPlugin('slicerFilter');
				this.own(aspect.after(this.slicerFilter.filterContainer, "_addFilter", lang.hitch(this, this._onAddFilter), true));
				this.own(aspect.after(this.slicerFilter.filterContainer, "_onQuery", lang.hitch(this, this._onQuery)));
			}

			return res;
		},

		/**
		 * Return Spec for given key
		 * @param {String} id
		 */
		getSpec: function(id) {
			return this._specs.getByQueryKey(id);
		},

		/**
		 * Returns label for specified Classifier Name and Value key. Used for filter select
		 * @param {string} name - classifier name
		 * @param {string} key - value key
		 * @return {string} - value (label)
		 */
		getSpecValueLabel: function(name, key) {
			var res = this._getQueryLabel(name, key);
			if (!res && this._slicer) { // make attempt to resolve the name in current slicer data (given classifier name may not be there)
				var slicer = this._slicer;
				var spec = this.getSpec(name);
				if (spec) {
					if (spec.type == xml.ATTR_REF) {
						res = slicer._storeGetName(slicer.store, key);
					} else {
						res = slicer._specFormatValue(spec, key);
					}
				}
			}
			return res == null ? key : res; // worse case scenario - return key instead of name. May only happen if it is fresh CV from old slicer.
		},

		/**
		 * Create Slicer container, slicer and editor.
		 * @param {Node} domNode
		 */
		_createSlicerContainer: function(domNode) {
			console.time("Slicer Container elements");
			if (this.slicerContainer) {
				this.slicerContainer.destroyRecursive();
			}
			this.slicerContainer = new BorderContainer({
				'class': 'csi-slicer-container',
				isLayoutContainer: true,
				gutters: false
			});

			var slicerEditorContainer = new ContentPane({
				region: 'right',
				style: 'display:none',
				'class': 'csi-slicer-editor-container'
			});
			this.slicerContainer.addChild(slicerEditorContainer);

			var SlicerGridContainer = new ContentPane({
				region: 'center',
				'class': 'csi-slicer-grid-container'
			});
			this.slicerContainer.addChild(SlicerGridContainer);
			console.timeEnd("Slicer Container elements");

			console.group("Slicer.Init");
			dndInit();
			this._slicer.init(this, SlicerGridContainer, this.store, this._slicerConfig, this._slicerEditor);
			this._showLastUpdatedDate();
			console.groupEnd();

			if (!this._slicer._grid) {
				return;
			}

			console.time("Slicer Editor");
			this._slicerEditor.init(slicerEditorContainer, this._slicerConfig, this);

			var editorVisible = storage.get(SLICER_EDITOR_VISIBLE);
			if (editorVisible) {
				this._slicerEditor.show(true);
			}
			console.timeEnd("Slicer Editor");

			console.time("Slicer Container staratup");
			this.slicerContainer.placeAt(domNode);
			this.slicerContainer.startup();
			console.timeEnd("Slicer Container staratup");

			if (editorVisible) {
				this._slicerEditor.restoreMetricPane(); // this should be called after startup
			}
		},

		/**
		 * Build the view. Called every time when settings were changed.
		 */
		_buildView: function(errMessage) {
			this._viewBuilt = false;

			if (this._slicerEditor) {
				this._slicerEditor.destroy();
				this._slicerEditor = null;
			}
			if (this._slicer) {
				this._slicer.destroy();
				this._slicer = null;
			}

			if (this.slicerContainer) {
				this.slicerContainer.destroy();
				this.slicerContainer = null;
			}

			if (errMessage) {
				// do not build view, just show error message
				this.inherited(arguments);
				html.showWait(false);
				console.error("ERROR: ", errMessage);
				return;
			}
			console.group("_buildView"); // EGOR DEBUG: change back to "groupCollapsed"
			console.time("Slicer build View");

			console.time("Slicer Config created");
			this._slicerConfig = new SlicerConfig();
			this._slicerConfig.widget = this;

			// Import "compare columns" setting from old slicer
			// We do back porting this in slicerEditor add/edit comparison
			if (this.compareTo && !this.compareTo.key && this.columnCompareKey) {
				this.compareTo = {key: this.columnCompareKey, show: true};
			}
			// Import "show totals" from old slicer
			if (this.columnTotalKey) {
				this.showTotals = {};
				this.showTotals[this.columnTotalKey] = true;
				this.columnTotalKey = null; // erase old value to prevent interference in the future. Only import once.
			}
			this._slicerConfig.importCfg(this.rowKeys, this.columnKeys, this.metricKeys, this.classifierStore,
				this.metricStore, this.metricsPos, this.metricsHl, this.metricsHlVisible, this.expandAll,
				this.stickyRows, this.columnSizes, this.compareTo, this.showTotals, this.flatSlicer);
			console.timeEnd("Slicer Config created");

			console.time("Slicer Created");
			this._slicer = new Slicer();
			console.timeEnd("Slicer Created");

			console.time("Slicer Editor created");
			this._slicerEditor = new SlicerEditor();
			console.timeEnd("Slicer Editor created");

			console.group("Slicer Container");
			console.time("Slicer Container created");
			this._createSlicerContainer(this.domNode);
			console.timeEnd("Slicer Container created");
			console.groupEnd();

			if (!this._slicer._grid) {
				// do not build view, just show error message
				this.inherited(arguments);
				html.showWait(false);
				console.error("Slicer grid creation failed");
				console.timeEnd("Slicer build View");
				console.groupEnd();
				return;
			}

			console.time("Slicer Subscription created");
			this._unsubscribeFromConfigChanges();
			this._subscribeToConfigChanges();
			console.timeEnd("Slicer Subscription created");

			console.time("Slicer Graphs created");
			this._resetGraphs();
			if (this.showGraphs) {
				this._renderGraphs();
			}
			console.timeEnd("Slicer Graphs created");

			console.time("Slicer widget inherited");
			this.inherited(arguments);
			console.timeEnd("Slicer widget inherited");

			this._viewBuilt = true;
			console.time("Slicer widget resize");
			this.resize();
			console.timeEnd("Slicer widget resize");

			html.showWait(false);

			console.timeEnd("Slicer build View");
			console.groupEnd();
		},

		/**
		 * Action "ShowEditor" function
		 * @param {ActionController} ac
		 */
		onActionShowEditor: function(ac) {
			var visible = !storage.get(SLICER_EDITOR_VISIBLE);
			this._slicerEditor.show(visible);
			storage.set(SLICER_EDITOR_VISIBLE, visible);
			this.forceResize();
		},

		/**
		* Return true if there are results for widget
		* @return {boolean}
		*/
		hasQueryResults: function() {
			return !!this.getRowStore();
		},

		/**
		 * Action "SetProperty" function
		 * @param {ActionController} ac
		 */
		onPropertyActionSetValue: function(ac) {
			// This function compare two arrays. Return true, if arrays are different.
			// Arrays have no difference if they have the same length and same set of members. Members order may be different.
			function arrHaveDiffMembers(arr1, arr2) {
				return (arr1.length != arr2.length) || (_.difference(arr1, arr2).length > 0);

			}
			// summary: set properties in actionValue on view
			var widget = ac.widget;
			// verify if new keys require server request
			var metricsStore = widget._slicer._config.metricStore;
			var newMetrics = ac.actionValue.metricKeys;
			var newRowCls = ac.actionValue.rowKeys;

			var mkeys = [];
			var ckeys = [];
			var bNewCustomMetric = false;
			_.each(newMetrics, function(mkey) {
				var metric = metricsStore.get(mkey);
				if (metric) {
					if (metric.dynamic) {
						mkeys = _.union(mkeys, widget.getInvolvedMetrics(metric));
						ckeys = _.union(ckeys, widget._getExpressionClassifiers(metric.func) || []);
					} else {
						mkeys.push(mkey);
					}
				} else { // just added custom metric
					bNewCustomMetric = true;
				}
			});
			mkeys = _.uniq(mkeys);
			ckeys = _.union(newRowCls, ac.actionValue.columnKeys);

			ac.actionValue.serverRequestRequired = bNewCustomMetric || (newRowCls.length) && (newMetrics.length) && (
				arrHaveDiffMembers(ckeys, widget._lastRequestKeys.Classifier) ||
				arrHaveDiffMembers(mkeys, widget._lastRequestKeys.Metric));
			widget.set(ac.actionValue);
		},

		/**
		 * Calculate slicer keys, send data request to server, then build view or show error
		 * @param {Response?} unused
		 */
		_onPreViewSearch: function() {
			var store = this.metricStore;
			if (!store || !this.metricKeys.length || !this.rowKeys.length) {
				// error with store or incomplete config - just draw empty grid
				this._lastRequestKeys = {Classifier: [], Metric: []};
				this._buildView();
				return;
			}

			html.showWait(true);
			// remove error message in the case if we had it in previous request
			this.set("contentMessage", "");
			html.destroyChildren(this.contentNode);

			// find all metric keys necessary for calculations - if there are dynamic expressions, then make sure to include
			// all keys referenced in those expressions
			var ckeys = [];
			var mkeys = [];

			_.each(this.metricKeys, function(mkey) {
				var metric = store.get(mkey);
				if (metric) {
					if (metric.dynamic) {
						if (metric.weight) {
							mkeys.push(mkey);
						}
						mkeys = _.union(mkeys, this.getInvolvedMetrics(metric));
						ckeys = _.union(ckeys, this._getExpressionClassifiers(metric.func) || []);
					} else {
						mkeys.push(mkey);
						// Add weight metric for Weighted Average Calculation
						if (metric.weightAttributeName) {
							mkeys.push(metric.id + csiSlicer.WEIGHT_METRIC_SUFFIX);
						}
					}
				}
			}, this);

			// compose options - always sort keys so that store comparison of options will return true even if rows and columns just change positions
			var options = {
				requestParameters: {
					Classifier: _.union(ckeys, this.rowKeys, this.columnKeys).sort(),
					Metric: _.uniq(mkeys).sort()
				}
			};

			this._lastRequestKeys = options.requestParameters;
			this._filteredData = null;

			console.time("Slicer server request");

			this.when(this.store.query(this.query, options),
				lang.hitch(this, function(data) {
					console.timeEnd("Slicer server request");
					// handle data response
					if (!data.total) {
						this.set("contentMessage", locale.resource("SLICER_DATA_EMPTY", [this.store.getDate()]));
					}

					this._filteredData = data;
					this._buildView();
					this._requestErrorProcessed = false;
				}),
				lang.hitch(this, function(err) {
					console.timeEnd("Slicer server request");
					if (!this._requestErrorProcessed && err.csiErrorCode?.match(/CLASSIFIER_NOT_FOUND|METRIC_NOT_FOUND/)) {
						// prevent endless circle, only attempt to resolve once
						this._requestErrorProcessed = true;
						// if classifier or metric not found, then refresh store before loading data
						this.when(this._resolveStore(true), function(view) {
							view._preView();
						});
					} else {
						// otherwise just show error message
						const csiErrorCode = err.csiErrorCode;
						const errMessage = err.message + (csiErrorCode ? "(" + csiErrorCode + ")" : "");
						this.set("contentMessage", errMessage);
						this._buildView(errMessage);
					}
				})
			);
		},

		/**
		 * Apply querySpecStore to existing store
		 * @param {string} name
		 * @param {any} oldValue
		 * @param {any} newValue
		 */
		_onWatchQuerySpecStore: function(name, oldValue, newValue) {
			this.store.querySpecStore = newValue;
		},

		/**
		 * Get store definition which is necessary to resolve view contents
		 * @return {Object}
		 */
		loadViewClasses: function() {
			return this._resolveStore(true);
		},

		/**
		 * Widget destroy: unsubscribe, delete objects, call inherited.
		 */
		destroy: function() {
			this._unsubscribeFromConfigChanges();
			delete this._slicerConfig;
			delete this._slicer;
			delete this._slicerEditor;

			this.inherited(arguments);
		},

		/**
		 * For filter: Return list of metric keys used in dynamic expression
		 * @param {string} expr
		 */
		_getExpressionClassifiers: function(expr) {
			var store = this.classifierStore;

			return csi.transform(store.query(), function(classifier) {
				var id = store.getIdentity(classifier);
				var re = new RegExp("\\b" + id + "\\b");
				return re.test(expr) ? id : null;
			});
		},

		/**
		 * For filter: Return list of metric keys used in dynamic expression
		 * @param {string} expr
		 */
		_getExpressionMetrics: function(expr) {
			var store = this.metricStore;

			return csi.transform(store.query(), function(metric) {
				var id = store.getIdentity(metric);
				var re = new RegExp("\\b" + id + "\\b");
				if (re.test(expr)) {
					if (metric.dynamic) {
						console.error("Dynamic metric expression can't reference dynamic or custom metrics: " + expr);
					} else {
						return id;
					}
				}

				return null;
			});
		},

		/**
		 * Return list of static metrics that required for given custom metrics calculation
		 * @param {Object} metric
		 * @param {boolean} includeDynamic - include dynamic metrics in result - dynamic metrics should
		 *  not be included in server requests but included in cube calculation. However, static metrics
		 *  inside dynamic should always be included
		 * @return {string[]} - array of metrics keys
		 */
		_getCustomMetricDependency: function(metric, includeDynamic) {
			var res = [];
			var params = ["calcFor", "metricA", "metricB", "calcForSingle", "costMetric", "revenueMetric"]; // Optional fields in presentation
			var store = this.metricStore;

			_.each(params, function(param) {
				var metrics = metric.presentation[param + "Node"];
				if (metrics) {
					_.each([].concat(metrics), function(metric) {
						var m = store.get(metric); // Get metric by Id
						if (m) {
							if (!m.dynamic || includeDynamic) {
								res.push(m.id);
							}
							if (m.dynamic) { // Custom metric can refer to dynamic metrics - these dynamic metrics should also be parsed
								res = res.concat(this._getExpressionMetrics(m.func) || []);
							}
						}
					}, this);
				}
			}, this);
			return res;
		},

		/**
		 * Return metrics that required for given metric calculation
		 * @param {Object} metric - metric object. See slicer/_base/_newStore() for metric object details
		 * @param {boolean} [includeDynamic] - include dynamic metrics in result
		 * @return {string[]} - array of metric keys
		 */
		getInvolvedMetrics: function(metric, includeDynamic) {
			if (metric.custom) {
				return this._getCustomMetricDependency(metric, includeDynamic);
			} else {
				return this._getExpressionMetrics(metric.func) || [];
			}

		},

		/**
		 * Filter related. Ensure that all settings are consistent with classifier and metric stores
		 */
		_resolvePreferences: function() {
			this.columnKeys = _.filter(this.columnKeys, this.classifierStore.get, this.classifierStore);
			this.rowKeys = _.filter(this.rowKeys, this.classifierStore.get, this.classifierStore);
			this.metricKeys = _.filter(this.metricKeys, this.metricStore.get, this.metricStore);

			// create Specs for all classifiers and metrics
			const specs = this._specs = new Specs();

			// FIX: should this become actual Renderer rather than just providing single function - may need to if we are going to render links;
			// but the problem is that we have no node context unless we create node every time from value
			const refRen = {formatValue: lang.hitch(this, this._getClassifierValueName)};

			const addSpec = (item) => {
				const {spec} = item;

				if (spec.type == xml.ATTR_REF) {
					// use special renderer to return name for ref and remove support for matching since values are really CNLs rather than strings
					spec.querySpec.ops = csiHeading.QUERY_OPS.ref;
					spec.ren = refRen;
				} else {
					spec.ren = csiHeading.getHeadingRenderer(null, item.heading);
				}

				spec.queryKey = item.id;
				specs.add(item.spec);
			};

			// add classifier specs
			this.classifierStore.query().forEach(addSpec);

			// create store of specs that can be filtered - make sure to do so before metrics are added and to exclude classifiers that are already filtered
			const filters = csi.hashArray(this.defView.slicerFilters, "key", Boolean);
			const specStore = specs.getQueryStore(false, null, filters && function(id) {return !filters[id];});

			this.set("querySpecStore", specStore);

			// add metric specs
			this.metricStore.query().forEach(addSpec);
		},

		/**
		 * Resolve store and then settings with store
		 * @param {boolean} bRefresh
		 * @return {Object}
		 */
		_resolveStore: function(bRefresh) {
			this.classifierStore = this.metricStore = null;

			return csiSlicer.getStore(this.defView.slicerStore, bRefresh).then(
				lang.hitch(this, function(store) {
					this.classifierStore = store.classifiers;
					this.metricStore = store.metrics;
					this._resolvePreferences();
					return this;
				}),
				lang.hitch(this, function(err) {
					// store does not exist
					this.set("contentMessage", err.message);
					return this;
				})
			);
		},

		_getQueryLabel: function(id, value) {
			if (this.queryLabels) {
				var cls = this.queryLabels[id];
				if (cls) {
					return cls[value];
				}
			}
			return null;
		},

		/**
		 * This function fire after filter completes processing new query
		 * Purpose of this - sanitize queryLevels, working together with function this._onAddFilter()
		 */
		_onQuery: function() {
			this.set("queryLabels", this._newQueryLabels);
			this._newQueryLabels = null;
		},

		/**
		 * This function runs after Filter processes new query, it will obtain key-label pairs and store it in CustomView.queryLabels variable
		 * @param {string} name - classifier name
		 * @param {string[]} key - array of classifier values keys
		 */
		_onAddFilter: function(name, key) {
			var qm = this.slicerFilter.queryMixin;
			var value = qm.getQueryValueAsLabel(name);
			if (!this._newQueryLabels) {
				this._newQueryLabels = {};
			}
			var newQueryLabels = this._newQueryLabels;
			for (var i = 0; i < key.length; i++) {
				if (!newQueryLabels[name]) {
					newQueryLabels[name] = {};
				}
				newQueryLabels[name][key[i]] = value[i];
			};
		},

		/**
		 * Obtain slicer settings, store, invoke filter
		 */
		_resolveView: function() {
			this.inherited(arguments);

			var defView = this.defView;

			this.rowKeys = _.pluck(defView.slicerRows, "key");
			this.columnKeys = _.pluck(defView.slicerColumns, "key");
			this.metricKeys = defView.slicerMetrics || [];
			this.graphMetricKey = this.metricKeys[0];

			this.watch("querySpecStore", lang.hitch(this, this._onWatchQuerySpecStore));

			this._propertyAction = this.defView._addViewAction("SetProperty", {
				m_onAction: this.onPropertyActionSetValue,
				m_postAction: "postActionRefresh"
			});

			// build data store for view
			var cache = this.cacheView;
			var node = this.getViewNode();
			var q = new ExtractQuery();

			_.each(defView.slicerFilters, function(filterObj) {
				var value = evaluate(filterObj.value, node, cache, true);
				if (value != null) {
					this.Filter(filterObj.key, xml.OP_EQUALS, value);
				}
			}, q);

			this.store = new Extract({
				filterQuery: q,
				requestParameters: {
					Name: defView.slicerStore
				}
			});
		},


		/**
		 * Refresh view after action process
		 * @param {ActionController} ac
		 */
		postActionRefresh: function(ac) {
			if (ac.actionValue.serverRequestRequired) {
				this.inherited(arguments);
			} else {
				if (this._slicerConfig.bQuickUpdate) {
					this._slicerConfig.bQuickUpdate = false;
					this._subscribeToConfigChanges();
				} else {
					this._buildView();
				}
			}
		},

		/**
		 * Callback function, called from SlicerConfig on new config set
		 * @param {Object} widget - instance of slicerWidget
		 */
		_updateProps: function(widget) {
			// prevent future updates until view get rebuilt
			widget._unsubscribeFromConfigChanges()
			// invoke action, get toolbar updated, get data updated
			var config = widget._slicerConfig.get();
			csiAction.invoke(widget, widget.domNode, widget._propertyAction, {
				actionValue: {
					columnKeys: config.columnClassifiers.slice(0),
					columnSizes: _.clone(config.columnSizes),
					compareTo: _.clone(config.compareTo),
					columnCompareKey: config.columnCompareKey,
					flatSlicer: config.flatSlicer,
					rowKeys: config.rowClassifiers.slice(0),
					metricKeys: config.metrics.slice(0),
					metricsPos: config.metricsPos,
					metricsHl: _.clone(config.metricsHl),
					metricsHlVisible: _.clone(config.metricsHlVisible),
					expandAll: config.expandAll,
					showTotals: _.clone(config.showTotals),
					stickyRows: config.stickyRows
				}
			});
		},

		/**
		 * Include our store to view widget _resetStores
		 */
		_resetStores: function() {
			if (this.store) {
				this.store.reset();
			}

			this.inherited(arguments);
		},

		/** for Excel action: return OrderedMemory store that can be used to show all classifiers or just those in view as columns
		 * @return {OrderedMemory}
		 */
		getRowStore: function() {
			return _getStore(this.classifierStore, this.rowKeys);
		},

		/**
		 * @return {OrderedMemory} - store that can be used to show all metrics or just those in view
		 */
		getMetricStore: function() {
			return _getStore(this.metricStore, this.metricKeys);
		},

		/**
		 * @param {string} url - variable url (store return url instead of name, and store have array of names, corresponding to the urls)
		 * @param {string} nameForNonUrl - name to be used if first parameter is not a valid url
		 * @return (string} - return value name
		 */
		getClassifierValueName: function(url, nameForNonUrl) {
			// summary: lookup name for url
			if (!xml.isValidURL(url)) {
				return nameForNonUrl;
			}

			var name = this.store.getName(url);
			if (name == null) {
				// this code should not be necessary for viewing slicer data, but may be necessary for the MultiFilter tooltip
				// if filtering by a ref attribute that is not a classifier in the data store
				var node = resolver.resolveNode(url, true);
				return node ? node.$Name : null;
			}

			return name || locale.getEmptyName();
		},

		/**
		 * Lookup name for url
		 * @param {string} url
		 * @param {string} unused - included to preserve function call format
		 * @return {string}
		 */
		_getClassifierValueName: function(url, unused) {
			return this.getClassifierValueName(url, "");
		},

		/**
		 * Hash values for given key
		 * @param {string} key
		 * @param {boolean} bAddTotal
		 * @return {Object}
		 */
		_getSortValueMap: function(key, bAddTotal) {
			var h = this.classifierStore.get(key).heading;

			// hash values for key in object, then extract keys to get unique values
			var values = _.keys(csi.hashArray(this._filteredData, String(this.store.getKeyIndex(key)), Boolean));

			// if enum, just use values in store rather than scanning all data
			if (h.getAttrType() == xml.ATTR_ENUM) {
				// sort values according to enum order
				// FIX: could change to use store indexProperty
				var enumValues = enums.getStore(xml.enumGetId(h._rca.getValue())).query();
				var sortOrder = csi.hashArray(enumValues, "id", Number);
				values = csi.sort(values, function(v) {return sortOrder[v];});
			} else if (h.getAttrType() == xml.ATTR_REF) {
				// sort values according to ref's name
				values = csi.sort(values, lang.hitch(this, this._getClassifierValueName));
			} else {
				// sort values alphabetically
				values.sort();
			}

			// add total as first value if desired
			if (bAddTotal) {
				values.unshift(TOTAL_VALUE);
			}

			// create map of values to index in array
			return csi.hashArray(values, null, Number);
		},

		/**
		 * Return unique number for combination of values - each key (starting from last) gets increasing
		 * importance using multiplier based on number of unique values for keys
		 * @param {Array} row
		 * @param {string[]} keys
		 * @param {Object[]} keyValueMaps
		 * @param {number[]} mapSizes
		 * @retun {number} id
		 */
		_getSortId: function(row, keys, keyValueMaps, mapSizes) {
			var id = 0;
			var multiplier = 1;
			var index, value, n;

			for (var i = keys.length - 1; i >= 0; --i) {
				index = this.store.getKeyIndex(keys[i]);
				value = row[index];
				n = keyValueMaps[i][value];
				id += n * multiplier;
				multiplier *= (mapSizes[i] + 1);
			}

			return id;
		},

		/**
		 * Given filtered data set and list of classifiers, generate data store containing items for each unique set of values
		 * with id that indicates sort order
		 * @param {boolean} bRows
		 * @param {string[]} keys
		 * @return {Memory}
		 */
		_getClassifierValueStore: function(bRows, keys) {
			keys = keys || (bRows ? this.rowKeys : this.columnKeys);

			// find distinct values for all classifier keys
			var keyValueMaps = _.map(keys, function(key) {
				return this._getSortValueMap(key, bRows);
			}, this);

			// store number of values for each map
			var keyValueSizes = _.map(keyValueMaps, _.size);

			// for each row of data, assign id to use for sorting and grouping
			var data = this._filteredData;
			var rowIndices = new Array(data.length);
			var rowIds = _.map(data, function(row, i) {
				rowIndices[i] = i;
				return this._getSortId(row, keys, keyValueMaps, keyValueSizes);
			}, this);

			// sort data by id
			rowIndices = csi.sort(rowIndices, function(i) {return rowIds[i];});

			// find keys to check when inserting total items - only do so for row classifier data store
			var totalClass, totalItem, beforeTotalKeys;

			if (bRows && this.rowTotalKey) {
				beforeTotalKeys = _.first(keys, _.indexOf(keys, this.rowTotalKey));
				totalClass = this.collapseRowTotal ? "csi-slicer-row-collapse" : "csi-slicer-row-total";
			}

			// create store items by grouping data by row id
			var storeItem, storeData = [];

			_.each(rowIndices, function(rowIndex) {
				var row = data[rowIndex];
				var id = rowIds[rowIndex];

				if (!storeItem || (storeItem.id != id)) {
					storeItem = {id: id, rows: []};

					// extract classifier values onto item - all rows should have same value, so just use first
					_.each(keys, function(key) {
						storeItem[key] = row[this.store.getKeyIndex(key)];
					}, this);

					// add total key if switching to new total group - check by testing for different values for keys appearing before total key
					if (beforeTotalKeys) {
						if (!totalItem || !_.isEqual(_.pick(totalItem, beforeTotalKeys), _.pick(storeItem, beforeTotalKeys))) {
							var bFoundTotal;

							totalItem = {
								id: "T" + id,
								rowClass: totalClass,
								rows: []
							};

							_.each(keys, function(key) {
								if (bFoundTotal) {
									totalItem[key] = "";
								} else if (key == this.rowTotalKey) {
									totalItem[key] = TOTAL_VALUE;
									bFoundTotal = true;
								} else {
									totalItem[key] = row[this.store.getKeyIndex(key)];
								}
							}, this);

							storeData.push(totalItem);
						}
					}

					if (!beforeTotalKeys || !this.collapseRowTotal) {
						storeData.push(storeItem);
					}
				}

				storeItem.rows.push(row);

				if (totalItem) {
					totalItem.rows.push(row);
				}
			}, this);

			return new Memory({data: storeData});
		},

		/**
		 * Create/ReCreate GraphsWidget
		 */
		_renderGraphs: function() {
			html.showWait(true);

			this._resetGraphs();

			this._graphsWidget = new GraphsWidget({
				"class": "csi-slicer-graphs",
				metricKey: this.get("graphMetricKey"),
				settings: this.get("graphSettings"),
				slicerDataAccess: new SlicerDataAccessAdapter(this),
				slicerWidget: this
			}, html.create("div", null, this.contentNode, "first"));
			this._graphsWidget.startup();
		},

		/**
		 * Destroy graphs, assign null to the reference
		 */
		_resetGraphs: function() {
			if (this._graphsWidget)
				this._graphsWidget.destroyRecursive();
			this._graphsWidget = null;
		},

		/**
		 * Return true if view built without errors and is ready for use
		 * @returns {bolean}
		 */
		_isViewReady: function() {
			return this._viewBuilt && this._slicer;
		},

		forceResize: function() {
			if (!this._isViewReady()) {
				return;
			}
			this.slicerContainer.resize();
			this._slicer._grid.resize();
			this.gridResize();
		},

		/**
		 * @override
		 * Resize view
		 */
		_onResize: function() {
			this.forceResize();
		},

		/**
		 * Calculate and set grid height. This function works on grid creation, page resize, data load completion, tree expand and collapse
		 */
		gridResize: function() {
			// The case when resize called before init or before biewBuild completion
			if (!this._isViewReady()) {
				return;
			}
			console.time(" -- Grid Resize");

			var BORDER_ALLOWANCE = 2; // 1px border on top and 1px on bottom = 2px;

			var grid = this._slicer._grid;
			var containerNode = this.slicerContainer.domNode;
			var jContainerNode = $(containerNode);

			// Determine horizontal scroll bar height. If no scroll bar then height=0
			var scrollbarHeight = html.getHorizontalScrollbarHeight(); // Standard scroll bar height
			const scrollers = grid._columnSetScrollers;
			const {flatSlicer} = this;
			const scroller = flatSlicer ? scrollers[0] : scrollers[1];
			var bHScroll = scroller.offsetWidth < scroller.children[0].offsetWidth; // Is there horizontal scroll bar on the grid?
			var scrollerHeight = bHScroll ? scrollbarHeight : 0;

			// Calculate footer + horizontal scroller summary height
			var footerHeight = grid.footerNode.offsetHeight;
			var tableBottom = scrollerHeight + footerHeight;

			// Set height to "auto" to allow the grid to straighten out, so we can read required sizes
			jContainerNode.find(".dgrid-content").height("auto");

			jHeader = jContainerNode.find(".dgrid-header");
			jFirstColumn = jContainerNode.find(".dgrid-column-set-0");
			jFooter = jContainerNode.find(".dgrid-footer");

			// now determine required and available height
			var gridHeight = jHeader.outerHeight() + jContainerNode.find(".dgrid-content").outerHeight() + tableBottom;
			var availablePageSpace = html.getAvailableHeight(this._graphsWidget ? this._graphsWidget.domNode : this.slicerContainer.domNode) - BORDER_ALLOWANCE;
			var bVScroll = false;
			if (gridHeight > availablePageSpace) {
				gridHeight = availablePageSpace;
				bVScroll = true;
			}
			// Set horizontal box-shadow if we have vertical scrolling
			jHeader.toggleClass('csi-slicer-scroll', bVScroll);
			// Set vertical box-shadow if we have horizontal scrolling
			jFirstColumn.toggleClass('csi-slicer-scroll', bHScroll);
			jFooter.toggleClass('csi-slicer-scroll', bVScroll);

			// Set slicer container height (BorderContainer). It contains Slicer Grid and Slicer Editor.
			// Slicer container height is always == AvailbaleHeight. And Slicer Editor have the same height too.
			domStyle.set(containerNode, {height: availablePageSpace + "px"});
			// Set slicer grid height
			domStyle.set(grid.domNode, {height: gridHeight + "px"});
			// Set dgrid footer position. We have to do  this, because we manually moved footer above horizontal scroll bar.
			domStyle.set(grid.footerNode, {
				bottom: scrollerHeight + "px",
				right: scrollbarHeight + "px"
			});

			this.slicerContainer.resize();
			console.timeEnd(" -- Grid Resize");
		},

		/**
		 * Attribute setter for ShowGraphs
		 * Show/hide graphs widget, create if missing
		 * @param {boolean} show
		 */
		_setShowGraphsAttr: function(show) {
			this._set("showGraphs", show);

			this._resetGraphs();

			if (show) {
				this._renderGraphs();
			}

			// We do full page resize here because adding/removing graphs will increase page height
			// and contentView's vertical scroll bar may appear - we need to recalculate content view sizes.
			page.resize();
		},

		/**
		 * Attribute setter for GraphMetric
		 * @param {string} value
		 */
		_setGraphMetricKeyAttr: function(value) {
			this._set("graphMetricKey", value);

			if (this._graphsWidget)
				this._graphsWidget.set("metricKey", value);
		},

		/**
		 * Attribute setter for GraphsSettgins
		 * @param {string} value
		 */
		_setGraphSettingsAttr: function(value) {
			this._set("graphSettings", value);

			if (this._graphsWidget)
				this._graphsWidget.set("settings", value);
		},

		/**
		 * Set function this._updateProps() as a call back to any configuration changes
		 */
		_subscribeToConfigChanges: function() {
			this._cfgHandle = this._slicerConfig.subscribe(this._updateProps);
		},

		/**
		 * Remove function this._updateProps() as a call back to any configuration changes
		 */
		_unsubscribeFromConfigChanges: function() {
			if (this._cfgHandle) {
				this._slicerConfig.unsubscribe(this._cfgHandle);
				this._cfgHandle = null;
			}
		},

		/**
		 * For excel: query limit does not apply to slicer
		 * @return {boolean}
		 */
		doesQueryTotalExceedMax: function() {
			return false;
		},

		_showLastUpdatedDate: function() {
			if (this._slicer.dataDate) {
				message.showInfo(locale.resource("SlicerLastUpdated") + " " + this._slicer.dataDate);
			}
		},

		onTabSwitch: function() {
			this.inherited(arguments);
			this._showLastUpdatedDate();
			this.forceResize();
		},

		/**
		 * Detect slicer missconfiguration when no metrices or now row classifiers selected
		 * Not the same and should not be changed/used to detect slicer error due to store/server errors
		 * If this._slicer == null (due to the errors) it should/will return false even thought there are no slicer data/metrics/classifiers
		 * @returns {boolean} return true is slicer is empty. 
		 */
		isEmpty: function() {
			const slicer = this._slicer;
			return slicer && slicer.bEmpty;
		}
	});
});
